import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaDownload, FaEye, FaTrash, FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const AdminDocuments = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  // ✅ Authentication check
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) navigate("/");
  }, [navigate]);

  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);

  // ✅ Fetch only Active Users
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    axios
      .get(`${API_BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        // FILTER ONLY ACTIVE USERS HERE ⬇⬇⬇
        const activeUsers = res.data.filter(
          (user) => user.status === "Active"
        );

        setUsers(activeUsers);
        setFilteredUsers(activeUsers);
      })
      .catch((err) => console.error("Error fetching users:", err));
  }, []);

  // ✅ Search filter
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredUsers(users); // show all
    } else {
      setFilteredUsers(users.filter((u) => u.id === Number(searchTerm)));
    }
  }, [searchTerm, users]);


  // ✅ Fetch documents for a user
  const fetchDocuments = (user) => {
    const token = sessionStorage.getItem("token");
    setSelectedUser(user);

    axios
      .get(`${API_BASE_URL}/api/uploaddocuments/user/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setDocuments(res.data);
        setShowModal(true);
      })
      .catch((err) => console.error("Error fetching documents:", err));
  };

  // ✅ Download document
  const downloadDocument = (docId, fileName) => {
    const token = sessionStorage.getItem("token");

    axios({
      url: `${API_BASE_URL}/api/uploaddocuments/download/${docId}`,
      method: "GET",
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
      })
      .catch((err) => console.error("Download error:", err));
  };

  // ✅ View document
  const viewDocument = (docId) => {
    const token = sessionStorage.getItem("token");

    axios({
      url: `${API_BASE_URL}/api/uploaddocuments/view/${docId}`,
      method: "GET",
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        const file = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
      })
      .catch((err) => console.error("View error:", err));
  };

  // ✅ Delete document
  const deleteDocument = (docId) => {
    const token = sessionStorage.getItem("token");
    if (!window.confirm("Are you sure you want to delete this document?")) return;

    axios
      .delete(`${API_BASE_URL}/api/uploaddocuments/delete/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        alert("✅ Document deleted successfully");
        setDocuments((prev) => prev.filter((d) => d.id !== docId));
      })
      .catch((err) => console.error("Delete error:", err));
  };

  return (
    <>
      {/* Fixed Navbar */}
      <div className="fixed-top">
        <AdminNavbar />
      </div>

      <div
        className="d-flex flex-column bg-light"
        style={{
          minHeight: "100vh",
          paddingTop: "125px",
          overflowX: "hidden",
        }}
      >
        <div className="flex-grow-1 px-4 pb-5">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="fw-bold text-dark mb-0">📂 Admin - User Documents</h3>
          </div>

          {/* Dropdown Filter */}
          <div className="mb-4" style={{ maxWidth: "300px" }}>
            <label className="form-label fw-semibold">Select Employee</label>
            <select
              className="form-select"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            >
              <option value="">All Employees</option>

              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>


          {/* Users Table */}
          <div className="card shadow-sm border-0">
            <div className="card-body p-0">
              <div
                className="table-responsive"
                style={{ maxHeight: "65vh", overflowY: "auto" }}
              >
                <table className="table table-hover align-middle text-start mb-0">
                  <thead className="table-secondary sticky-top">
                    <tr>
                      <th>Employee Name</th>
                      <th>Email</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <tr key={user.id}>
                          <td className="text-start">{user.name}</td>
                          <td className="text-start">{user.email}</td>
                          <td className="text-start">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => fetchDocuments(user)}
                            >
                              <i className="bi bi-folder2-open"></i> View Documents
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-muted py-4">
                          No employees found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Documents Modal */}
          {showModal && (
            <div
              className="modal fade show"
              style={{
                display: "block",
                backgroundColor: "rgba(0,0,0,0.5)",
                paddingTop: "120px",
              }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content shadow-lg">
                  <div className="modal-header bg-light">
                    <h5 className="modal-title">
                      Documents for{" "}
                      <span className="text-primary">{selectedUser?.name}</span>
                    </h5>
                    <button
                      className="btn-close"
                      onClick={() => setShowModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    {documents.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-bordered align-middle text-start">
                          <thead className="table-light">
                            <tr>
                              <th>Type</th>
                              <th>File Name</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {documents.map((doc) => (
                              <tr key={doc.id}>
                                <td className="text-start">{doc.name}</td>
                                <td className="text-start">{doc.originalFileName}</td>
                                <td>
                                  <button
                                    className="btn btn-outline-success btn-sm me-2 text-start"
                                    onClick={() => viewDocument(doc.id)}
                                  >
                                    <FaEye /> View
                                  </button>
                                  <button
                                    className="btn btn-outline-primary btn-sm me-2 text-start"
                                    onClick={() =>
                                      downloadDocument(doc.id, doc.originalFileName)
                                    }
                                  >
                                    <FaDownload /> Download
                                  </button>
                                  <button
                                    className="btn btn-outline-danger btn-sm text-start"
                                    onClick={() => deleteDocument(doc.id)}
                                  >
                                    <FaTrash /> Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted mb-0 text-center">
                        No documents uploaded for this user.
                      </p>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminDocuments;
